// Drawer de "Subir nuevo producto" — se abre desde la tarjeta placeholder que encabeza
// la grilla de productos (ver `onSubirProducto` en `PickProductPanel`).
//
// Existe para que dar de alta un producto NO se lleve la pantalla entera: el flujo
// original navega al step `upload-product`, que reemplaza la selección de productos y
// obliga a volver. En el layout apilado del Catálogo esa navegación era especialmente
// cara —la grilla es el centro de la pantalla— así que el alta pasa a un panel lateral
// que se monta encima y devuelve al mismo lugar al cerrarse.
//
// Adentro va el MISMO `UploadProductPanel` que usa el flujo de siempre (estado
// explicativo con ejemplos y tips → grilla de fotos → nombre → Confirmar), así que no
// hay una segunda versión de esa pantalla que mantener. El upload real corre acá, con
// `uploadNewProduct`, igual que en `MultiSlotProductPickerSheet`.
//
// El chrome (portal + overlay + bottom sheet en mobile / side panel en desktop, con la
// animación de entrada y salida) es el mismo de `MultiSlotProductPickerSheet`.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useExitTransition } from '../../../hooks/useExitTransition';
import { uploadNewProduct } from '../productUploadApi';
import { UploadProductPanel } from './UploadProductPanel';
import { AgregarVariosModal } from '../../../components/AgregarVariosModal';

export interface SubirProductoInlineProps {
  /** id_company para el upload. Sin esto el confirm muestra un error. */
  userCompanyId: string | null;
  /** Marca activa — se asocia al producto creado. */
  brandId: string | null;
  /** Rubro del usuario — define las fotos de ejemplo del estado explicativo. */
  rubro: string | null;
  /** Plan del usuario — el popup de alta en bloque lo usa para el tope por tanda. */
  userPlan?: string | null;
  /** Terminó el alta: el host refresca el catálogo. */
  onProductsRefreshed: () => void;
  /** Salir del alta (terminó o se canceló). El host decide a dónde vuelve. */
  onSalir: () => void;
  /**
   * El host se hace cargo del alta EN BLOQUE: en vez del popup propio de acá, "Subir
   * varios productos" avisa y el host decide dónde mostrarlo
   * (ADR-2026-09-22-el-alta-en-bloque-tambien-adentro-del-selector). Lo usa el selector
   * de fuentes, que lo dibuja en su misma ventana. Sin esto, el popup lo abre este
   * componente, como siempre.
   */
  onSubirVarios?: () => void;
  /**
   * El alta ocupa una ventana ANCHA y se dibuja en dos columnas con su barra de CTA
   * (ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas). Lo pide el
   * selector de fuentes de la caja del Agente; el drawer de acá abajo y el panel de
   * Modelo v2 no lo pasan.
   */
  ancho?: boolean;
  /** Sin el título del panel: el host ya pone el suyo (el drawer de acá abajo). */
  sinEncabezado?: boolean;
}

/**
 * El alta de un producto SIN chrome: el panel y el upload real, nada más.
 *
 * Lo usan los dos hosts: el drawer de acá abajo —que le pone overlay y panel flotante— y
 * el panel lateral de Modelo v2, que lo monta en el lugar de la grilla, como el picker de
 * modelos hace con "crear" y "subir" (ADR-2026-08-25-subir-producto-en-el-mismo-panel).
 */
export const SubirProductoInline: React.FC<SubirProductoInlineProps> = ({
  userCompanyId,
  brandId,
  rubro,
  userPlan = null,
  onProductsRefreshed,
  onSalir,
  onSubirVarios,
  ancho = false,
  sinEncabezado = false,
}) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingName, setPendingName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const handleAddMoreFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingFiles((prev) => [...prev, ...arr]);
  };

  const handleRemoveFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleConfirmUpload = async () => {
    if (uploading || pendingFiles.length === 0) return;
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      await uploadNewProduct({
        companyId: userCompanyId,
        userId: user.id,
        name: pendingName,
        files: pendingFiles,
        brandId,
      });
      // El producto NO queda seleccionado solo: esto da de alta, y elegirlo sigue siendo
      // un tap en la grilla. Refrescamos para que aparezca ahí —el orden por defecto es
      // "últimos subidos", así que cae primero— y salimos.
      onProductsRefreshed();
      setPendingFiles([]);
      setPendingName('');
      onSalir();
    } catch (err) {
      console.error('[SubirProductoInline] upload error', err);
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el producto. Probá de nuevo.',
      );
    } finally {
      setUploading(false);
    }
  };

  // Terminó el alta en bloque (el popup se cierra solo): refrescamos y salimos, que ya no
  // hay nada que mostrar acá.
  const handleBulkSaved = () => {
    onProductsRefreshed();
    onSalir();
  };

  return (
    <>
      {/* variant='desktop' también en mobile: arma header / scroll / footer fijos con su
          `px-6` interno, que es el layout correcto para un panel. El variant='mobile' es
          un flex-col sin padding, pensado para cuando el host pone el suyo. */}
      <UploadProductPanel
        variant="desktop"
        files={pendingFiles}
        name={pendingName}
        onNameChange={setPendingName}
        onAddMore={handleAddMoreFiles}
        onRemoveFile={handleRemoveFile}
        onCancel={onSalir}
        onConfirm={handleConfirmUpload}
        uploading={uploading}
        error={uploadError}
        rubro={rubro}
        onBulkUpload={() => (onSubirVarios ? onSubirVarios() : setBulkOpen(true))}
        ancho={ancho}
        sinEncabezado={sinEncabezado}
        // El "Volver" interno duplicaría la salida que ya pone el host (la X del panel o
        // su encabezado).
        hideBack
      />
      {/* zIndex 220 > el z-211 del drawer: sino el popup queda por debajo. Con
          `onSubirVarios` no se monta: el alta en bloque la muestra el host. */}
      {!onSubirVarios && (
      <AgregarVariosModal
        open={bulkOpen}
        companyId={userCompanyId}
        brandId={brandId}
        userPlan={userPlan ?? ''}
        zIndex={220}
        onClose={() => setBulkOpen(false)}
        onSaved={handleBulkSaved}
      />
      )}
    </>
  );
};

export interface SubirProductoDrawerProps {
  open: boolean;
  onClose: () => void;
  /** id_company para el upload. Sin esto el confirm muestra un error. */
  userCompanyId: string | null;
  /** Marca activa — se asocia al producto creado; sin esto no aparece al filtrar
   *  la grilla por marca. */
  brandId: string | null;
  /** Rubro del usuario — define las fotos de ejemplo del estado explicativo. */
  rubro: string | null;
  /** Plan del usuario — el popup de alta en bloque lo usa para el tope por tanda. */
  userPlan?: string | null;
  /** Terminó el alta. El host refresca el catálogo global para que el producto nuevo
   *  aparezca en la grilla de atrás. */
  onProductsRefreshed: () => void;
}

export const SubirProductoDrawer: React.FC<SubirProductoDrawerProps> = ({
  open,
  onClose,
  userCompanyId,
  brandId,
  rubro,
  userPlan = null,
  onProductsRefreshed,
}) => {
  const { mounted, animated } = useExitTransition(open);
  // "Subir varios" reemplaza el contenido del panel en vez de abrir un modal encima
  // (ADR-2026-10-06-subir-varios-adentro-del-panel). Mientras crea, el panel no se cierra:
  // irse en medio desmonta la tanda con las subidas en vuelo.
  const [vista, setVista] = useState<'alta' | 'varios'>('alta');
  const [variosOcupado, setVariosOcupado] = useState(false);
  const cerrar = () => {
    if (variosOcupado) return;
    onClose();
  };
  // Al volver a abrir arranca en el alta de un producto.
  React.useEffect(() => {
    if (open) setVista('alta');
  }, [open]);

  // Lo que se estaba subiendo vive en `SubirProductoInline` y se va con él: el drawer lo
  // desmonta recién al terminar la animación de salida (`mounted`), así que el panel no
  // se ve volver al estado explicativo mientras sale.
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={cerrar}
      />
      {/* Mismo panel que el alta de Mi marca (VariantForm): fondo de la pantalla, esquinas
          de 24px, 440 de ancho y el encabezado con bajada —
          ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. */}
      <div
        className={`fixed z-[211] bg-[#F9F9F9] flex flex-col transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[24px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))] shadow-[0_-12px_40px_-12px_rgba(16,24,40,0.3)]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[440px] md:rounded-[24px] md:shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] md:ring-1 md:ring-black/[0.04]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        }}
      >
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.12] rounded-full mx-auto" />
        </div>
        {vista === 'varios' ? (
          <AgregarVariosModal
            open
            enPanel
            companyId={userCompanyId}
            brandId={brandId}
            userPlan={userPlan ?? ''}
            // Los avisos de Tiendanube / Mercado Libre son ventanas: por encima del z-211.
            zIndex={220}
            onClose={() => setVista('alta')}
            onCerrarPanel={cerrar}
            onOcupado={setVariosOcupado}
            // `onSaved` refresca (llega también cuando algunos fallaron y quedan para
            // reintentar); el panel se cierra sólo con la tanda entera creada.
            onSaved={onProductsRefreshed}
            onTerminado={onClose}
          />
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 px-6 pb-2 md:pt-6 shrink-0">
              <div className="min-w-0">
                <h3 className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[#16181D]">
                  Agregar producto
                </h3>
                <p className="mt-1 text-[13.5px] leading-snug text-black/45">
                  Subí sus fotos y empezá a crear con él
                </p>
              </div>
              <button
                type="button"
                onClick={cerrar}
                className="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.05] text-black/50 hover:bg-black/[0.09] hover:text-black/80 transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <SubirProductoInline
                userCompanyId={userCompanyId}
                brandId={brandId}
                rubro={rubro}
                userPlan={userPlan}
                onProductsRefreshed={onProductsRefreshed}
                onSalir={onClose}
                onSubirVarios={() => setVista('varios')}
                sinEncabezado
              />
            </div>
          </>
        )}
      </div>
    </>,
    document.body,
  );
};
